import styled, { css } from 'styled-components/macro'
import palette from 'src/designSystem/variables/palette'
import BootstrapNav from 'react-bootstrap/Nav'
import BootstrapContainer from 'react-bootstrap/Container'
import BootstrapRow from 'react-bootstrap/Row'
import BootstrapTab from 'react-bootstrap/Tab'
import { color, compose, grid, textStyle, typography } from 'styled-system'
import { themeGet } from '@styled-system/theme-get'

export const TabContainer = styled(BootstrapContainer)
  .withConfig({
    shouldForwardProp: (prop, defaultValidatorFn) =>
      defaultValidatorFn(prop) || prop === 'fluid',
  })
  .attrs({ fluid: true })<{ $fillHeight?: boolean }>`
  grid-column: 1 / -1;
  ${grid}
  margin: 0;
  padding: 0;
  ${({ $fillHeight }) =>
    $fillHeight &&
    css`
      display: flex;
      flex-direction: column;
      min-height: 0;
      height: 100%;
    `}
`

export const TabRow = styled(BootstrapRow)<{
  $fillHeight?: boolean
  $content?: boolean
}>`
  margin: 0;
  ${({ $fillHeight, $content }) =>
    $fillHeight &&
    ($content
      ? css`
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        `
      : css`
          flex-shrink: 0;
        `)}
`

// The strip plus its scroll arrows. The Row's direct child, so it takes the
// width (and the 12px gap below) the strip itself used to.
export const TabNavScroller = styled.div`
  position: relative;
  padding: 0;
  margin-bottom: 12px;
`

export const TabNav = styled(BootstrapNav).attrs({
  variant: 'tabs',
})`
  flex-grow: 1;
  /* One line that scrolls sideways once the tabs outgrow it -- beside an open
     copilot, say -- rather than wrapping into extra rows. The scrollbar is
     hidden because TabScrollButton is the affordance. */
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  padding: 0 12px;
  /* A scroll container clips at its padding edge, which would shave off the
     1px a tab overlaps a bottom border by (bootstrap's -1px margin) and thin
     the active underline. An inset shadow draws the same line inside the box,
     under the underline, so the strip looks as it did. */
  border-bottom: none;
  box-shadow: inset 0 -1px 0 ${palette.neutral[100]};
  margin-bottom: 0;
`

// Shown at an edge only while tabs are hidden past it. The fade over the cut-off
// tab is what says there is more; it stops 1px short of the bottom so the
// strip's line runs on underneath.
// ponytail: the fade assumes a white page, the background every Tabs sits on
// today; take it from a prop if one ever sits on a tinted panel.
export const TabScrollButton = styled.button<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  bottom: 1px;
  ${({ $side }) => $side}: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: ${({ $side }) =>
    $side === 'left' ? 'flex-start' : 'flex-end'};
  width: 48px;
  padding: 0 8px;
  border: none;
  background: linear-gradient(
    to ${({ $side }) => ($side === 'left' ? 'right' : 'left')},
    ${palette.neutral.white} 55%,
    transparent
  );
  color: ${palette.neutral[600]};
  font-size: 14px;
  cursor: pointer;

  &:hover {
    color: ${palette.neutral[900]};
  }
`

export const TabTitle = styled(BootstrapNav.Link)`
  &.nav-link {
    display: flex;
    align-items: center;
    height: 40px;
    margin-bottom: 0;
    padding: 0;
    padding-left: ${themeGet('space.m')}px;
    padding-right: ${themeGet('space.m')}px;
    border: none;
    border-bottom: 4px solid transparent;
    border-top-left-radius: 2px;
    border-top-right-radius: 2px;
    color: ${themeGet('colors.text.med')};

    ${themeGet('textStyles.subtitle.1')}
    :hover {
      background-color: ${themeGet('colors.bg.primary')};
      border-bottom: 4px solid ${themeGet('colors.border.primary.base')};
    }

    &.active {
      border: none;
      border-bottom: 4px solid ${themeGet('colors.border.primary.dark')};
      color: ${themeGet('colors.text.default')};
      font-weight: ${themeGet('fontWeights.semiBold')};
    }

    .tab-title {
      ${typography}
    }
  }
`

export const TabContent = styled(BootstrapTab.Content)<{
  $fillHeight?: boolean
}>`
  flex-grow: 1;
  margin: 0;
  padding: 0;
  ${({ $fillHeight }) =>
    $fillHeight &&
    css`
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      & > .tab-pane.active {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }
    `}
`

export const TabNote = styled.div.attrs({
  py: 'l',
  color: 'fill.quaternary.dark',
  textStyle: 'link.2',
})`
  ${compose(color, textStyle)}
  display: flex;
  align-items: center;
  min-height: 48px;
  border-radius: 2px;

  && ul {
    margin: 0;
    padding-left: 25px;
  }
`

export const TabNavItem = styled(TabNav.Item)`
  cursor: pointer;
  flex-shrink: 0;
`

export const TabSpinnerContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`

export const TabCard = styled.div`
  border-radius: 12px;
  border: 1px solid ${palette.neutral[100]};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
`
